/**
 * 任务队列2.0 常量配置
 * 
 * @description 定义队列系统的常量和配置策略
 * @author Frontend Architect
 * @version 2.0.0
 */

import { 
  UserType, 
  QueueStage, 
  type UserTypeStrategy, 
  type QueueManagerConfig 
} from '@/types/taskQueue';

/** 随机数生成工具 */
export const randomBetween = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/** 获取随机排队数量 */
export const getRandomQueueNum = (): number => randomBetween(75, 250);

/** 获取随机减少百分比 */
export const getRandomReductionPercentage = (): number => randomBetween(3, 6);

/** 免费用户策略配置 */
const FREE_USER_STRATEGY: UserTypeStrategy = {
  userType: UserType.FREE,
  maxTotalWaitTime: Number.MAX_SAFE_INTEGER, // 免费用户无总时间限制
  stages: {
    [QueueStage.INITIALIZING]: {
      name: '启动中',
      maxWaitTime: 20,
      countToTotalTime: true,
      config: {
        showProgressBar: false,
        message: '生图任务正在启动中'
      }
    },
    [QueueStage.QUEUING]: {
      name: '排队中',
      maxWaitTime: Number.MAX_SAFE_INTEGER, // 免费用户排队无时间限制
      countToTotalTime: true,
      config: {
        initialQueueRange: [75, 250],
        reductionInterval: 5000, // 5秒
        reductionPercentageRange: [3, 6],
        minReduction: 5,
        showProgressBar: false,
        messageTemplate: '当前排队 {queueNum} 名'
      }
    },
    [QueueStage.MODEL_LOADING]: {
      name: '模型加载中',
      maxWaitTime: 0, // 免费用户无此阶段
      countToTotalTime: false,
      config: {
        enabled: false
      }
    },
    [QueueStage.GENERATING]: {
      name: '生图中',
      maxWaitTime: Number.MAX_SAFE_INTEGER,
      countToTotalTime: false, // 实际生图时间不计入等待时间
      config: {
        maxProgressPercentage: 90, // 最大进度90%
        showProgressBar: true,
        message: '正在生成中...'
      }
    },
    [QueueStage.COMPLETED]: {
      name: '已完成',
      maxWaitTime: 0,
      countToTotalTime: false,
      config: {
        showProgressBar: false,
        message: '生成完成'
      }
    },
    [QueueStage.ERROR]: {
      name: '生成失败',
      maxWaitTime: 0,
      countToTotalTime: false,
      config: {
        showProgressBar: false,
        message: '生成失败，请重试'
      }
    }
  }
};

/** 标准版会员策略配置 */
const VIP_USER_STRATEGY: UserTypeStrategy = {
  userType: UserType.VIP,
  maxTotalWaitTime: 50, // 标准版最多等待50秒
  stages: {
    [QueueStage.INITIALIZING]: {
      name: '启动中',
      maxWaitTime: 5,
      countToTotalTime: true,
      config: {
        showProgressBar: false,
        message: '生图任务正在启动中'
      }
    },
    [QueueStage.QUEUING]: {
      name: '排队中',
      maxWaitTime: 30,
      countToTotalTime: true,
      config: {
        // 假排队数据（仅展示用）
        fakeQueueRange: [75, 250],
        // 实际排队配置
        actualQueueRange: [3, 6],
        actualWaitTimeRange: [3, 6],
        showProgressBar: false,
        messageTemplate: '当前排队 {queueNum} 名'
      }
    },
    [QueueStage.MODEL_LOADING]: {
      name: '模型加载中',
      maxWaitTime: 15,
      countToTotalTime: true,
      config: {
        enabled: true,
        showProgressBar: true,
        message: '正在加载模型资源中'
      }
    },
    [QueueStage.GENERATING]: {
      name: '生图中',
      maxWaitTime: Number.MAX_SAFE_INTEGER,
      countToTotalTime: false,
      config: {
        maxProgressPercentage: 90,
        showProgressBar: true,
        message: '正在生成中...'
      }
    },
    [QueueStage.COMPLETED]: {
      name: '已完成',
      maxWaitTime: 0,
      countToTotalTime: false,
      config: {
        showProgressBar: false,
        message: '生成完成'
      }
    },
    [QueueStage.ERROR]: {
      name: '生成失败',
      maxWaitTime: 0,
      countToTotalTime: false,
      config: {
        showProgressBar: false,
        message: '生成失败，请重试'
      }
    }
  }
};

/** 专业版会员策略配置 */
const SVIP_USER_STRATEGY: UserTypeStrategy = {
  userType: UserType.SVIP,
  maxTotalWaitTime: 30, // 专业版最多等待30秒
  stages: {
    [QueueStage.INITIALIZING]: {
      name: '启动中',
      maxWaitTime: 3,
      countToTotalTime: true,
      config: {
        showProgressBar: false,
        message: '生图任务正在启动中'
      }
    },
    [QueueStage.QUEUING]: {
      name: '排队中',
      maxWaitTime: 15,
      countToTotalTime: true,
      config: {
        // 假排队数据（仅展示用）
        fakeQueueRange: [75, 250],
        // 实际排队配置
        actualQueueRange: [1, 3],
        actualWaitTimeRange: [1, 4],
        showProgressBar: false,
        messageTemplate: '当前排队 {queueNum} 名'
      }
    },
    [QueueStage.MODEL_LOADING]: {
      name: '模型加载中',
      maxWaitTime: 10,
      countToTotalTime: true,
      config: {
        enabled: true,
        showProgressBar: true,
        message: '正在加载模型资源中'
      }
    },
    [QueueStage.GENERATING]: {
      name: '生图中',
      maxWaitTime: Number.MAX_SAFE_INTEGER,
      countToTotalTime: false,
      config: {
        maxProgressPercentage: 90,
        showProgressBar: true,
        message: '正在生成中...'
      }
    },
    [QueueStage.COMPLETED]: {
      name: '已完成',
      maxWaitTime: 0,
      countToTotalTime: false,
      config: {
        showProgressBar: false,
        message: '生成完成'
      }
    },
    [QueueStage.ERROR]: {
      name: '生成失败',
      maxWaitTime: 0,
      countToTotalTime: false,
      config: {
        showProgressBar: false,
        message: '生成失败，请重试'
      }
    }
  }
};

/**
 * 画板 / AI 创作排队副文案展示用（与 SVIP_USER_STRATEGY 排队阶段 config 一致）。
 * 专业版（svip）、旗舰版（ultra）使用范围值，避免展示精确名次。
 */
export const BOARD_QUEUE_SVIP_RANK_DISPLAY_RANGE = [1, 3] as const;
/** 与付费策略排队阶段 fakeQueueRange 一致（展示用总池范围） */
export const BOARD_QUEUE_PAID_TOTAL_DISPLAY_RANGE = [75, 250] as const;

/** 队列管理器默认配置 */
export const DEFAULT_QUEUE_CONFIG: QueueManagerConfig = {
  userStrategies: {
    [UserType.FREE]: FREE_USER_STRATEGY,
    [UserType.VIP]: VIP_USER_STRATEGY,
    [UserType.SVIP]: SVIP_USER_STRATEGY
  },
  queueReduction: {
    interval: 5000, // 5秒间隔
    reductionPercentageRange: [3, 6],
    minReduction: 5
  },
  tracking: {
    enabled: true
  },
  debug: process.env.NODE_ENV === 'development'
};

/** 阶段显示文案映射 */
export const STAGE_MESSAGES = {
  [QueueStage.INITIALIZING]: '生图任务正在启动中',
  [QueueStage.QUEUING]: '当前排队 {queueNum} 名',
  [QueueStage.MODEL_LOADING]: '正在加载模型...',
  [QueueStage.GENERATING]: '正在生成图片...',
  [QueueStage.COMPLETED]: '生成完成',
  [QueueStage.ERROR]: '生成失败，请重试'
} as const;

/** 默认排队等待时间计算（每人30秒） */
export const DEFAULT_WAIT_TIME_PER_PERSON = 30;

/** 进度条更新间隔（毫秒） */
export const PROGRESS_UPDATE_INTERVAL = 100;

/** 心跳检测间隔（毫秒） */
export const HEARTBEAT_INTERVAL = 30000; 